import { useEffect, useState } from "react";
import { api } from "../api";
import {
  UNKNOWN_SHA,
  frontendSha,
  isDirtySha,
  isKnownSha,
  normalizeSha,
  shortSha,
} from "../lib/buildStamp";

/**
 * The version header of the user menu: which commit this BUNDLE is, and which
 * commit the BACKEND it is talking to is.
 *
 * IT IS A HEADER REGION, NOT A MENU ITEM. It renders as the popover's first
 * child, outside the list of `role="menuitem"` buttons, so it stays above every
 * button no matter which ones the viewer's privileges reveal — there is no
 * ordering for it to lose. `role="presentation"` keeps it out of the menu's
 * roving-focus and typeahead sets (the same treatment `@norkron/web-menu` gives
 * `MenuLabel`).
 *
 * WHY IT EXISTS: served assets are content-hashed with no commit stamp, so the
 * live deployment could not be tied back to a git SHA. Both values here are
 * REAL build stamps — the bundle's from Vite `define` at build time, the
 * server's from `GET /api/health`, which publishes the binary's baked
 * `build_stamp.sha`. Neither is hand-maintained, and neither can be bumped by
 * editing a constant.
 *
 * `unknown` IS SHOWN AS WRONG, ON PURPOSE. It gets the danger colour and an
 * explicit label rather than a plausible-looking stand-in, because a version
 * that looks authoritative and is not is worse than no version at all.
 */

/** How long the backend fetch may hang before we call it unknown. */
const HEALTH_TIMEOUT_MS = 8_000;

type BackendState =
  | { readonly status: "checking" }
  | { readonly status: "resolved"; readonly sha: string };

/**
 * Fetch the backend's build stamp ONCE per session, the first time the menu is
 * opened. Cached at module scope: the server's build cannot change without the
 * page being reloaded, so re-opening the menu must not re-request.
 */
let cachedBackendSha: string | null = null;

export function useBackendSha(enabled: boolean): BackendState {
  const [state, setState] = useState<BackendState>(() =>
    cachedBackendSha === null
      ? { status: "checking" }
      : { status: "resolved", sha: cachedBackendSha },
  );

  useEffect(() => {
    if (!enabled || cachedBackendSha !== null) return;
    let live = true;
    const timer = setTimeout(() => {
      if (live) setState({ status: "resolved", sha: UNKNOWN_SHA });
    }, HEALTH_TIMEOUT_MS);

    void api
      .health()
      .then((health) => {
        // A backend deployed before `version` existed answers without it. That
        // normalises to `unknown` — never to something that reads as a commit.
        const sha = normalizeSha(health.version);
        cachedBackendSha = sha;
        if (live) setState({ status: "resolved", sha });
      })
      .catch(() => {
        if (live) setState({ status: "resolved", sha: UNKNOWN_SHA });
      })
      .finally(() => clearTimeout(timer));

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [enabled]);

  return state;
}

/** Reset the session cache. Test-only seam; never called by the app. */
export function resetBackendShaCacheForTest(): void {
  cachedBackendSha = null;
}

interface VersionRowProps {
  readonly label: string;
  /** `null` while the value is still being fetched. */
  readonly sha: string | null;
  readonly testId: string;
}

function VersionRow({ label, sha, testId }: VersionRowProps) {
  const pending = sha === null;
  const known = !pending && isKnownSha(sha);
  const dirty = !pending && isDirtySha(sha);

  // Three visibly DIFFERENT states. "checking" is not a version and does not
  // pretend to be one; "unknown" is coloured as an error, not as data.
  const text = pending ? "checking…" : known ? shortSha(sha) : UNKNOWN_SHA;
  const state = pending ? "pending" : known ? "known" : "unknown";

  // Native `title` only — the browser owns its placement, so this cannot
  // violate the house rule that tooltips never sit left or right of a control.
  const hint = pending
    ? undefined
    : known
      ? dirty
        ? `${sha} — built with uncommitted changes to tracked files`
        : sha
      : "This build did not record a commit, so it cannot be identified.";

  return (
    <div className="menuver__row">
      <span className="menuver__label">{label}</span>
      <span
        className="menuver__value"
        data-testid={testId}
        data-state={state}
        title={hint}
      >
        {text}
      </span>
    </div>
  );
}

export interface MenuVersionsProps {
  /** The bundle's commit. Defaults to the value Vite injected at build time. */
  readonly frontend?: string;
  /** The server's commit; `null` while `GET /api/health` is in flight. */
  readonly backend: string | null;
}

export function MenuVersions({ frontend, backend }: MenuVersionsProps) {
  const front = normalizeSha(frontend ?? frontendSha());
  return (
    // `role="presentation"` carries NO aria-label: ARIA prohibits naming an
    // element whose role has been removed, and a name there is simply dropped.
    // The two rows read as "frontend <sha>" / "backend <sha>" from their own
    // text, which is what a screen reader announces.
    <div className="menuver" data-testid="menu-versions" role="presentation">
      <VersionRow label="frontend" sha={front} testId="menu-version-frontend" />
      <VersionRow label="backend" sha={backend} testId="menu-version-backend" />
    </div>
  );
}
